import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/admin/LogoAndName.svg";
import Loading from "../../components/layout/Loading";
import { resetPassword } from "../../actions/AuthAction";
import { toast, ToastContainer } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const CreatePassword = () => {
  const PASSWORD_MAX_LENGTH = 20;
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({ temp: "", new: "", confirm: "" });
  const [isLoading, setIsLoading] = useState(false);
  const email = localStorage.getItem("username");

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%?&])[A-Za-z\d@#$!%?&]{8,20}$/;

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);

    if (value && !passwordRegex.test(value)) {
      setErrors((prev) => ({
        ...prev,
        new: `Password must be at least 8-${PASSWORD_MAX_LENGTH} chars, include uppercase, lowercase, number, and special character.`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, new: "" }));
    }

    if (confirmPassword && value !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirm: "Passwords do not match." }));
    } else if (confirmPassword) {
      setErrors((prev) => ({ ...prev, confirm: "" }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value && value !== newPassword) {
      setErrors((prev) => ({ ...prev, confirm: "Passwords do not match." }));
    } else {
      setErrors((prev) => ({ ...prev, confirm: "" }));
    }
  };

  const handleReset = async () => {
    if (errors.new || errors.confirm || !newPassword || !confirmPassword) {
      toast.error("Please fix validation errors before submitting.");
      return;
    }

    setIsLoading(true);
    const token = localStorage.getItem("token");

    try {
      const data = await resetPassword({
        username: email,
        newPassword,
        tokenOrTempPassword: token,
      });

      if (data.success) {
        toast.success(data.message || "Password reset successfully");
        localStorage.removeItem("token");

        setTimeout(() => navigate("/signin"), 1500);
      } else {
        // toast.error("Failed to reset password.");
      }
    } catch (err) {
      // toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleReset();
  };

  return (
    <div className="flex items-center justify-center min-h-screen mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center space-y-6 w-full max-w-md">
        <img src={logo} alt="Logo" className="mx-auto p-2" />

        <div>
          <h2 className="text-text1 text-3xl leading-9 font-extrabold">
            Create your password
          </h2>
          <p className="text-gray-500 text-[14px] leading-tight mt-2 ">
            Set a strong new password to secure your account.
          </p>
        </div>

        {!isLoading ? (
          <div className="space-y-6">
            {/* New Password */}
            <div className="text-left">
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New password"
                  className={`base_input w-full p-2 pr-10 border rounded-md ${
                    errors.new ? "border-red-500" : "border-gray-300"
                  }`}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  onKeyDown={handleKeyDown}
                  maxLength={PASSWORD_MAX_LENGTH}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={
                    showNewPassword ? "Hide new password" : "Show new password"
                  }
                >
                  {showNewPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {errors.new && (
                <p className="text-[#e23131] text-sm mt-1">{errors.new}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="text-left">
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  className={`base_input w-full p-2 pr-10 border rounded-md ${
                    errors.confirm ? "border-red-500" : "border-gray-300"
                  }`}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onKeyDown={handleKeyDown}
                  maxLength={PASSWORD_MAX_LENGTH}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirmPassword ? (
                    <Eye size={18} />
                  ) : (
                    <EyeOff size={18} />
                  )}
                </button>
              </div>
              {errors.confirm && (
                <p className="text-[#e23131] text-sm mt-1">{errors.confirm}</p>
              )}
            </div>

            {/* Reset Button */}
            <button
              className={`bg-primary text-text1 rounded-md p-4 w-full mt-4 font-medium ${
                !newPassword || !confirmPassword
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleReset}
              disabled={!newPassword || !confirmPassword}
            >
              Confirm
            </button>
          </div>
        ) : (
          <Loading />
        )}
        <ToastContainer
          position="top-right z-40"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
        />
      </div>
    </div>
  );
};

export default CreatePassword;
